import React, { useEffect, useMemo, useState } from "react";
import { Icons, Box, Button, Card, FormControlLabel, Stack, Switch } from "@curves/ui-base";

import useInBodyApi from "@apis/useInBodyApi";
import { Link } from "react-router-dom";
import { CLUB_INBODY_ROUTER } from "@icoach/router/routerPath";
import { IN_BODY_INTERVIEW_TABS, MEASURE_REFERENCE_TYPE } from "@icoach/constants/";
import clsx from "clsx";
import ProfileImageUploader, { ProfileImageDisplay } from "@icoach/components/ProfileImageUploader";
import useMemberApi from "@apis/useMemberApi";
import InBodyInterviewNotMeasureDialog from "@icoach/inbody/dialog/InBodyInterviewNotMeasureDialog";
import InBodyMeasurementTriggerButton from "@icoach/inbody/components/InBodyMeasurementTriggerButton";
import { MemberInfoCard } from "@icoach/components";
import { WorkoutMetrics } from "@icoach/memberDetail/components";
import CommonTabs from "@icoach/components/layout/CommonTabs";
import { HealthDeclarationTip } from "@icoach/inbody/components/";
import { isEmpty, isFunction } from "@util/utils";

/** ================================
 * 📌 會員摘要
 *  ================================
 */
const InbodyInterviewMemberProfileImageDisplay = props => {
	const { imageUrl: imageUrlProps, memberID: memberIDProps } = props;
	const { postMemberTranscriptAvatarApi } = useMemberApi();
	const [imageUrl, setImageUrl] = useState(null);

	useEffect(
		() => {
			if (imageUrlProps) {
				setImageUrl(imageUrlProps);
			}
		},
		// eslint-disable-next-line
		[imageUrlProps],
	);

	const handleImageUpload = ({ blob, previewImage }) => {
		const formData = new FormData();
		formData.append("file", blob);
		postTranscriptAvatar(previewImage, memberIDProps, formData);
	};

	const postTranscriptAvatar = async (previewImage, memeberID, params) => {
		const resp = await postMemberTranscriptAvatarApi(memeberID, params);
		if (resp) {
			setImageUrl(previewImage);
		}
	};

	return (
		<Box className={"inbody-interview-member-profile-image"}>
			<Box className={"inbody-interview-member-profile-image-wrap"}>
				<ProfileImageDisplay imageUrl={imageUrl} />
				<ProfileImageUploader buttonText={"設定形象照"} onImageUpload={handleImageUpload} />
			</Box>
		</Box>
	);
};

// 切換是否要量身
const SwitchIsMeasureBox = ({ isInterviewNotMeasure = false, memberID, updateMeasureDay }) => {
	const { putInterviewNotMeasureStatusApi } = useInBodyApi();

	const doPutInterviewNotMeasureStatusApi = async params => {
		let resp = await putInterviewNotMeasureStatusApi(params);
		if (resp) {
			isFunction(updateMeasureDay) &&
				updateMeasureDay({
					isInterviewNotMeasure: params.isInterviewNotMeasure,
				});
		}
	};

	const handleIsMeasureOnChange = (e, isInterview) => {
		let params = {
			memberID,
			isInterviewNotMeasure: isInterview,
		};
		doPutInterviewNotMeasureStatusApi(params);
	};

	return (
		<Box className={"is-measure"}>
			<FormControlLabel
				label="只面談不量身"
				key={isInterviewNotMeasure}
				control={<Switch defaultChecked={isInterviewNotMeasure} />}
				className="mr-3 mb-1"
				labelPlacement="start"
				onChange={handleIsMeasureOnChange}
			/>
		</Box>
	);
};

const InBodyInterviewMemberInfoSummary = ({ sourceData: memberProfile, peopleID, activeTab, updateMeasureDay, className }) => {
	const { memberName, nickName, memberNo, proPicturePath, picturePath, memberID, totalWorkoutQuantity, weeklyWorkoutQuantity, monthlyWorkoutQuantity, isInterviewNotMeasure, resource } =
		memberProfile || {};

	const { deviceOrderPageUrl } = resource || {};

	const tabData = useMemo(
		() => {
			return [
				{
					label: "會員摘要",
					value: IN_BODY_INTERVIEW_TABS.SUMMARY,
					component: Link,
					to: CLUB_INBODY_ROUTER.GET_INTERVIEW_PATH(IN_BODY_INTERVIEW_TABS.SUMMARY, peopleID),
				},
				{
					label: "比較表",
					value: IN_BODY_INTERVIEW_TABS.COMPARE,
					component: Link,
					to: CLUB_INBODY_ROUTER.GET_INTERVIEW_PATH(IN_BODY_INTERVIEW_TABS.COMPARE, peopleID),
				},
				{
					label: "量身紀錄",
					value: IN_BODY_INTERVIEW_TABS.MEASURE,
					component: Link,
					to: CLUB_INBODY_ROUTER.GET_INTERVIEW_PATH(IN_BODY_INTERVIEW_TABS.MEASURE, peopleID),
				},
				{
					label: "面談內容",
					value: IN_BODY_INTERVIEW_TABS.NOTES,
					component: Link,
					to: CLUB_INBODY_ROUTER.GET_INTERVIEW_PATH(IN_BODY_INTERVIEW_TABS.NOTES, peopleID),
				},
			];
		},
		// eslint-disable-next-line
		[peopleID],
	);

	return (
		<Card className={clsx("member-summary-box", className)}>
			<Card.CardContent className={"pt-3"}>
				<InbodyInterviewMemberProfileImageDisplay imageUrl={proPicturePath ?? picturePath} memberID={memberID} />
				<Box className={"member-summary-box__container"}>
					<Stack justifyContent={"start"} alignItems={"start"} spacing={2}>
						<MemberInfoCard name={memberName} nickname={nickName} memberNo={memberNo} isShowAvatar={false} />
						<WorkoutMetrics weeklyWorkoutQuantity={weeklyWorkoutQuantity} monthlyWorkoutQuantity={monthlyWorkoutQuantity} totalWorkoutQuantity={totalWorkoutQuantity} />
						<Box>
							<Box className={"tool-btn-group text-right mr-3"}>
								<Button component={Link} to={CLUB_INBODY_ROUTER.GET_HISTORY_PATH(MEASURE_REFERENCE_TYPE.MEMBER, peopleID)} variant={"contained"}>
									返回列表
								</Button>
							</Box>
							<Box className={"controller-box"}>
								<SwitchIsMeasureBox isInterviewNotMeasure={isInterviewNotMeasure} memberID={memberID} updateMeasureDay={updateMeasureDay} />
								<HealthDeclarationTip memberID={memberID}>{isInterviewNotMeasure ? <InBodyInterviewNotMeasureDialog /> : <InBodyMeasurementTriggerButton />}</HealthDeclarationTip>
							</Box>
						</Box>
					</Stack>
				</Box>
				{!isEmpty(deviceOrderPageUrl) && (
					<Box className={"measure-outside-box"}>
						<Button href={deviceOrderPageUrl} className="ml-2" target="_blank" rel="noopener noreferrer" variant="outlined" size="xsmall" startIcon={<Icons.Ring htmlColor={"#653ac2"} />}>
							i私教資訊
						</Button>
					</Box>
				)}
			</Card.CardContent>
			<CommonTabs tabs={tabData} activeTab={activeTab} variant="scrollable" scrollButtons="auto" className="px-1" />
		</Card>
	);
};

export default InBodyInterviewMemberInfoSummary;
